import { inc } from 'app/modules/shared/helpers'
import { ReportFilterSection, ReportFilterSectionType } from '../../components/filter/types'
import { DisassemblyFilterStat, DisassemblySection } from '../../features/disassembly/types'
import { SignalGroupVerdict } from '../../types'
import { castStrengthToVerdict } from '../../helpers'
import BaseFormatter from './base'
import { combineSignalGroups } from './helper'

// Only signals coming from disassembly instructions reference a disassembly section's dataUUID.
// Used to avoid matching signals of other data types that may share a UUID.
export const DISASSEMBLY_ORIGIN = 'disassemblySections'

// A signal group linked to a section, plus the exact instruction text that triggered it
// (parsed from signalReadable, e.g. 'Found "cpuid" instruction' -> 'cpuid'). null if unknown.
type LinkedSignal = { verdict: SignalGroupVerdict; instruction: string | null }

/**
 * Format data needed for Disassembly Sections report subpage.
 */
export default class DisassemblyFormatter extends BaseFormatter {
  sectionsCache: { [key: string]: boolean } = {}

  /**
   * Format original report data
   *
   * @param data
   * @param report
   */
  format(data: { [key: string]: any }, report: { [key: string]: any }): void {
    this.sectionsCache = {}

    const resource = this.getResource(data, 'file')
    const hasData = resource && Array.isArray(resource.disassemblySections) && resource.disassemblySections.length
    const stat: DisassemblyFilterStat = { types: { lib: {}, api: {}, signal: {} }, total: 0 }
    const filterSections: { [sectionName: string]: ReportFilterSection } = {
      search: { type: ReportFilterSectionType.Search, name: 'search', items: [] },
      lib: { type: ReportFilterSectionType.Flags, name: 'lib', items: [], title: 'library' },
      api: { type: ReportFilterSectionType.Flags, name: 'api', items: [] },
      signal: { type: ReportFilterSectionType.Flags, name: 'signal', items: [], title: 'threat-indicators' },
    }

    if (!hasData) {
      return
    }

    // Threat indicators live in signal groups elsewhere in the report. Build a
    // lookup of section dataUUID -> linked signal groups, then attach to each section.
    const signalGroups = combineSignalGroups(data.allSignalGroups ?? [], data.resources)
    const signalsByDataUUID = this.buildSignalsByDataUUID(signalGroups)

    for (let i = 0; i < resource.disassemblySections.length; i++) {
      const section: DisassemblySection = resource.disassemblySections[i]

      const linked = signalsByDataUUID.get(section.dataUUID)
      if (linked?.length) {
        this.attachSignalGroups(section, linked)
      }

      this.gatherFilterSections(section, filterSections)
      this.gatherFilterStat(section, stat)
    }

    report.disassembly = {
      data: resource.disassemblySections,
      filter: { stat, sections: Object.values(filterSections) },
    }
  }

  gatherFilterStat(section: DisassemblySection, stat: DisassemblyFilterStat) {
    const statTypes = stat.types
    const apis = section.apis || []
    const libs = section.libs || []
    const signalGroups = section.signalGroups || []

    for (const api of apis) {
      statTypes.api[api] = inc(statTypes.api[api])
    }
    for (const lib of libs) {
      statTypes.lib[lib] = inc(statTypes.lib[lib])
    }
    for (const group of signalGroups) {
      statTypes.signal[group.signalId] = inc(statTypes.signal[group.signalId])
    }

    stat.total = inc(stat.total)
  }

  gatherFilterSections(section: DisassemblySection, sections: { [sectionName: string]: ReportFilterSection }) {
    const apis = section.apis || []
    const libs = section.libs || []
    const signalGroups = section.signalGroups || []

    for (const api of apis) {
      if (typeof this.sectionsCache[api] === 'undefined') {
        sections.api.items.push({ value: api, name: api })
        this.sectionsCache[api] = true
      }
    }
    for (const lib of libs) {
      if (typeof this.sectionsCache[lib] === 'undefined') {
        sections.lib.items.push({ value: lib, name: lib })
        this.sectionsCache[lib] = true
      }
    }
    for (const group of signalGroups) {
      if (typeof this.sectionsCache[group.signalId] === 'undefined') {
        sections.signal.items.push({ value: group.signalId, name: group.signalDescription })
        this.sectionsCache[group.signalId] = true
      }
    }
  }

  /**
   * Build a lookup of section dataUUID -> linked threat indicator signals.
   * Each signal carries the group verdict and the exact instruction it triggered on.
   *
   * @param signalGroups - combined signal groups from the report
   */
  buildSignalsByDataUUID(signalGroups: any[]): Map<string, LinkedSignal[]> {
    const map = new Map<string, LinkedSignal[]>()

    for (const group of signalGroups) {
      // Derived from threatLevel like the Threat Indicators formatter, so the section link's
      // `${signalId}-${verdict}` always matches that page's group key.
      const threatLevel = group.verdict?.threatLevel
      const verdict: SignalGroupVerdict = {
        verdict:
          typeof threatLevel === 'number' ? castStrengthToVerdict(threatLevel) : group.verdict?.verdict?.toLowerCase(),
        signalId: group.identifier,
        signalDescription: group.description,
      }

      for (const signal of group.signals ?? []) {
        if (!signal.dataUUID || !signal.originPath?.includes(DISASSEMBLY_ORIGIN)) {
          continue
        }

        const existing = map.get(signal.dataUUID) ?? []
        existing.push({ verdict, instruction: this.extractInstructionText(signal.signalReadable) })
        map.set(signal.dataUUID, existing)
      }
    }

    return map
  }

  /**
   * Attach threat indicators to a section: a deduped list for the section header/filter,
   * and per-instruction indicators on the exact instruction(s) that triggered them.
   */
  attachSignalGroups(section: DisassemblySection, linked: LinkedSignal[]) {
    // Section-level: unique signal groups, used for the header block and filter.
    const groups: SignalGroupVerdict[] = []
    for (const { verdict } of linked) {
      if (!groups.some((g) => g.signalId === verdict.signalId)) {
        groups.push(verdict)
      }
    }
    section.signalGroups = groups

    // Instruction-level: mark the exact instruction that raised each signal. The data has
    // no instruction index, so when the same instruction text repeats, all matches are marked.
    for (const instruction of section.instructions) {
      for (const { verdict, instruction: instText } of linked) {
        if (!instText || instText !== instruction.inst) {
          continue
        }

        const list = instruction.signalGroups || (instruction.signalGroups = [])
        if (!list.some((g) => g.signalId === verdict.signalId)) {
          list.push(verdict)
        }
      }
    }
  }

  // Parse the instruction text out of a signalReadable, e.g. 'Found "cpuid" instruction' -> 'cpuid'
  extractInstructionText(signalReadable?: string): string | null {
    const match = signalReadable?.match(/"([^"]+)"/)
    return match ? match[1] : null
  }
}
